import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdKeyboardArrowDown } from 'react-icons/md';
import './Select.css';

export interface SelectOption<T = string> {
  value: T;
  label: string;
}

interface SelectProps<T = string> {
  label?: string;
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

const Select = <T extends string | number>({
  label,
  value,
  options,
  onChange,
  placeholder,
  className = '',
  id,
  disabled = false
}: SelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const IconComponent = MdKeyboardArrowDown as React.ComponentType<{ className?: string }>;

  const updateMenuPosition = () => {
    if (!selectRef.current) return;
    const rect = selectRef.current.getBoundingClientRect();
    setMenuStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : (placeholder || '');

  const handleOptionClick = (optionValue: T) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdown = isOpen && !disabled && menuStyle ? (
    <div ref={dropdownRef} className="select-dropdown select-dropdown-portal" style={menuStyle}>
      {placeholder && !selectedOption && (
        <div className="select-option select-option-placeholder" onClick={(e) => e.stopPropagation()}>
          {placeholder}
        </div>
      )}
      {options.map((option) => (
        <div
          key={String(option.value)}
          className={`select-option ${option.value === value ? 'select-option-selected' : ''}`}
          onClick={() => handleOptionClick(option.value)}
        >
          {option.label}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className={`select-wrapper ${className}`}>
      {label && (
        <label htmlFor={id} className="select-label">
          {label}
        </label>
      )}
      <div 
        ref={selectRef}
        className={`select-container ${isOpen ? 'select-open' : ''} ${disabled ? 'select-disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="select-display">
          <span className={`select-value ${!selectedOption ? 'select-placeholder' : ''}`}>
            {displayValue}
          </span>
          <IconComponent className={`select-arrow ${isOpen ? 'select-arrow-open' : ''}`} />
        </div>
      </div>
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default Select;
